import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { List } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { useLocation } from "react-router-dom";

import { useUserContext } from "../../contexts/UserContext";
import useMenuQuery from "../../hooks/graphql-hooks/queries/useMenuQuery";
import { MenuItem } from "../../models/autogenerated-deliveryService-gql/graphql";
import "../../vendor/perfect-scrollbar.scss";
import { mapMenuItemsToNavItems } from "./SideBarItems";
import SidebarNavSection from "./SidebarNavSection";

const baseScrollbar = (props: any) => css`
  background-color: ${props.theme.sidebar.background};
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  flex-grow: 1;
`;

const Scrollbar = styled.div`
  ${baseScrollbar}
`;

const PerfectScrollbar = styled(ReactPerfectScrollbar)`
  ${baseScrollbar}
`;

const Items = styled.div`
  padding-top: ${(props) => props.theme.spacing(2.5)};
  padding-bottom: ${(props) => props.theme.spacing(2.5)};
`;

type SidebarNavProps = {
  authorized: boolean;
};

const SidebarNav: React.FC<SidebarNavProps> = ({ authorized }) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const routerLocation = useLocation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const ScrollbarComponent = (
    matches ? PerfectScrollbar : Scrollbar
  ) as React.ElementType;

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const menuQuery = useMenuQuery({
    skip: !authorized,
    dataAction: (data) => setMenuItems(data),
  });

  React.useEffect(() => {
    if (!authorized) return;
    menuQuery.refetch();
  }, [user.role, authorized, routerLocation.key, routerLocation.pathname]);

  const items = mapMenuItemsToNavItems(
    t,
    menuItems.length > 0 ? menuItems : undefined,
    user.role,
  );

  return (
    <ScrollbarComponent>
      <List disablePadding>
        <Items>
          {items &&
            items.map((item) => (
              <SidebarNavSection
                component="div"
                key={item.title}
                pages={item.pages}
                title={item.title}
              />
            ))}
        </Items>
      </List>
    </ScrollbarComponent>
  );
};

export default SidebarNav;
